/**
 * Hardware Provisioning Service
 * API calls for hardware management
 */

import api from './api';
import type {
  Hardware,
  HardwareFormData,
  PhoneAccountFormData,
  ButtonConfig,
  Manufacturer,
  PhoneModel,
  PhoneType,
} from '../components/Hardware/types';

// Use api as apiClient for consistency
const apiClient = api;

const BASE_URL = '/endpoints/hardware';

export const hardwareService = {
  // Hardware CRUD
  async getAll(customerId?: number): Promise<Hardware[]> {
    const params = customerId ? { customerId } : {};
    const response = await apiClient.get(BASE_URL, { params });
    return response.data.data;
  },

  async getById(id: number, customerId?: number): Promise<Hardware> {
    const params = customerId ? { customerId } : {};
    const url = customerId ? `${BASE_URL}/${id}?${new URLSearchParams(params as any).toString()}` : `${BASE_URL}/${id}`;
    const response = await apiClient.get(url);
    return response.data.data;
  },

  async create(data: HardwareFormData, customerId?: number): Promise<{ hardwareId: number }> {
    const params = customerId ? { customerId } : {};
    const url = customerId ? `${BASE_URL}?${new URLSearchParams(params as any).toString()}` : BASE_URL;
    const response = await apiClient.post(url, data);
    return response.data.data;
  },

  async update(id: number, data: Partial<HardwareFormData>): Promise<void> {
    await apiClient.put(`${BASE_URL}/${id}`, data);
  },

  async delete(id: number, customerId?: number): Promise<void> {
    const params = customerId ? { customerId } : {};
    const url = customerId
      ? `${BASE_URL}/${id}?${new URLSearchParams(params as any).toString()}`
      : `${BASE_URL}/${id}`;
    await apiClient.delete(url);
  },

  async transfer(id: number, targetCustId: number): Promise<void> {
    await apiClient.post(`${BASE_URL}/${id}/transfer`, { targetCustId });
  },

  // Phone Accounts
  async addAccount(hardwareId: number, data: PhoneAccountFormData, customerId?: number): Promise<void> {
    const params = customerId ? { customerId } : {};
    const url = customerId
      ? `${BASE_URL}/${hardwareId}/accounts?${new URLSearchParams(params as any).toString()}`
      : `${BASE_URL}/${hardwareId}/accounts`;
    await apiClient.post(url, data);
  },

  async updateAccount(accountId: number, enabled: boolean, customerId?: number): Promise<void> {
    const params = customerId ? { customerId } : {};
    const url = customerId
      ? `${BASE_URL}/accounts/${accountId}?${new URLSearchParams(params as any).toString()}`
      : `${BASE_URL}/accounts/${accountId}`;
    await apiClient.put(url, { enabled });
  },

  async deleteAccount(accountId: number, customerId?: number): Promise<void> {
    const params = customerId ? { customerId } : {};
    const url = customerId
      ? `${BASE_URL}/accounts/${accountId}?${new URLSearchParams(params as any).toString()}`
      : `${BASE_URL}/accounts/${accountId}`;
    await apiClient.delete(url);
  },

  // Line Keys
  async updateLineKey(hardwareId: number, buttonNo: number, config: ButtonConfig, isExpansion: boolean = false, customerId?: number): Promise<void> {
    const params = customerId ? { customerId } : {};
    const url = customerId
      ? `${BASE_URL}/${hardwareId}/linekeys/${buttonNo}?${new URLSearchParams(params as any).toString()}`
      : `${BASE_URL}/${hardwareId}/linekeys/${buttonNo}`;
    await apiClient.put(url, { ...config, isExpansion });
  },

  async deleteLineKey(hardwareId: number, buttonNo: number, isExpansion: boolean = false, customerId?: number): Promise<void> {
    const params = customerId ? { customerId } : {};
    const url = customerId
      ? `${BASE_URL}/${hardwareId}/linekeys/${buttonNo}?${new URLSearchParams(params as any).toString()}`
      : `${BASE_URL}/${hardwareId}/linekeys/${buttonNo}`;
    await apiClient.delete(url, { data: { isExpansion } });
  },

  // Lookup Data
  async getManufacturers(): Promise<Manufacturer[]> {
    const response = await apiClient.get(`${BASE_URL}/manufacturers`);
    return response.data.data;
  },

  async getModels(): Promise<PhoneModel[]> {
    const response = await apiClient.get(`${BASE_URL}/models`);
    return response.data.data;
  },

  async getPhoneTypes(): Promise<PhoneType[]> {
    const response = await apiClient.get(`${BASE_URL}/types`);
    return response.data.data;
  },

  // Extensions lookup
  async getExtensions(): Promise<Array<{ extension: string; name: string }>> {
    const response = await apiClient.get('/endpoints/extensions', {
      params: { userOnly: false }
    });
    return response.data?.extensions || [];
  },
};
